/**
 * The teacher-facing reading of a track job (2026-09-26, "Add a track").
 * Pure, so the words a teacher sees mid-lesson are pinned by a test rather
 * than by a screenshot. The queue itself — timing, claims, which files a job
 * produces — lives in functions/trackJobs.js.
 */

const lower = (s) => String(s || '').trim().toLowerCase();
const toMillis = (v) => {
	if (v == null) return null;
	if (typeof v === 'number') return v;
	if (typeof v.toMillis === 'function') return v.toMillis();
	return null;
};

// Mirrors ALIVE_WITHIN_MS in functions/trackJobs.js (pinned by
// trackJobsModel.test.js reading that file): a Mac is ON when it checked in
// within the last minute and did not sign off.
export const ALIVE_WITHIN_MS = 60 * 1000;

export const isMacOn = (worker, nowMs = Date.now()) => {
	if (!worker || worker.kind === 'cloud' || worker.state === 'stopped') return false;
	const seen = toMillis(worker.lastSeenAt);
	return seen != null && nowMs - seen <= ALIVE_WITHIN_MS;
};

export const macsOn = (workers = [], nowMs = Date.now()) =>
	(Array.isArray(workers) ? workers : []).filter((w) => isMacOn(w, nowMs));

/** A saved video's kind says whether its vocals come out: a Karaoke video is
 *  already instrumental; a Lyrics or Performance video is the recording. */
export const removeVocalsForVideo = (kind) => kind !== 'karaoke';

/**
 * Where a track made from THIS link for THIS song stands, from the newest
 * such job: 'idle' (none, or it was dismissed by failing and can be retried
 * via 'failed'), 'working', 'failed', or 'done'. Lets the video dock show the
 * state on the button itself and refuse a second tap that would make the
 * same track twice.
 */
export const linkTrackState = (jobs = [], song = null, url = null) => {
	if (!url) return 'idle';
	const mine = jobsForSong(jobs, song)
		.filter((j) => j.kind === 'link' && j.source && j.source.url === url)
		.sort((a, b) => (toMillis(b.createdAt) ?? Infinity) - (toMillis(a.createdAt) ?? Infinity));
	if (!mine.length) return 'idle';
	return jobTone(mine[0]);
};

/** The same line for a pasted link: only a studio Mac can fetch one. */
export const linkAvailabilityText = (workers = [], nowMs = Date.now()) => {
	const on = macsOn(workers, nowMs).map((w) => w.name || 'A studio Mac');
	if (on.length === 0) return 'No studio Mac is on right now. A link waits until one is, then it is fetched.';
	const who = on.length === 1 ? `${on[0]} is on and will` : `${on.slice(0, -1).join(', ')} and ${on[on.length - 1]} are on and one will`;
	return `${who} fetch the link. With vocals out, allow about half the length of the song.`;
};

/** The line under the Remove vocals toggle: who will do the work. The cloud
 *  figure is MEASURED, not estimated: a 60 s cut took 56 s warm / 67 s cold on
 *  the 4-vCPU separator in production, 2026-09-26. A Mac runs BS-RoFormer since
 *  2026-09-27: 107 s for a 213 s song on the M4 Max, so about half the song. */
export const availabilityText = (workers = [], nowMs = Date.now()) => {
	const on = macsOn(workers, nowMs).map((w) => w.name || 'A studio Mac');
	if (on.length === 0) return 'No studio Mac is on right now, so the cloud will remove the vocals. It takes about as long as the song.';
	if (on.length === 1) return `${on[0]} is on and will remove the vocals in about half the length of the song.`;
	return `${on.slice(0, -1).join(', ')} and ${on[on.length - 1]} are on. The vocals come out in about half the length of the song.`;
};

// Anthony, 2026-09-26: "If there's ever an issue and something doesn't work,
// you should put up a message that says to notify me immediately." Every
// failure a teacher can see in Add a track / Save key ends with this line;
// the technical detail goes underneath, small, for Anthony when he is told.
export const TELL_ANTHONY = 'Please tell Anthony right away.';

// How long a finished job stays on screen as "Added", so the teacher sees it
// land and then it gets out of the way.
export const DONE_VISIBLE_MS = 90 * 1000;

/** The jobs that belong under this song tab: same registry id, else same name. */
export const jobsForSong = (jobs = [], song = null) => {
	if (!song) return [];
	const id = song.canonicalSongId || song.songId || null;
	const name = lower(song.name || song.songName);
	return (Array.isArray(jobs) ? jobs : []).filter(
		// A songLyrics job (2026-10-02) names its repertoire row exactly.
		(j) => j && ((song.id && j.repertoireId === song.id) || (id && j.songId === id) || (name && lower(j.songName) === name))
	);
};

/** Worth a row right now: anything unfinished, anything failed, and a job
 *  filed in the last DONE_VISIBLE_MS. Newest first. */
export const visibleJobs = (jobs = [], nowMs = Date.now(), dismissed = new Set()) =>
	(Array.isArray(jobs) ? jobs : [])
		.filter((j) => j && !dismissed.has(j.id))
		.filter((j) => {
			if (j.status === 'queued' || j.status === 'claimed' || j.status === 'failed') return true;
			if (j.status === 'cancelled') {
				const at = toMillis(j.cancelledAt) ?? toMillis(j.updatedAt);
				return at != null && nowMs - at < STOPPED_VISIBLE_MS;
			}
			if (j.status === 'done') {
				if (!j.pointerId) return true; // done by the worker, not yet filed
				const at = toMillis(j.filedAt) ?? toMillis(j.updatedAt);
				return at == null || nowMs - at < DONE_VISIBLE_MS;
			}
			return false;
		})
		.sort((a, b) => (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0));

/**
 * May a teacher STOP this job? Mirrors canCancel in functions/trackJobs.js
 * (pinned by trackJobsModel.test.js): a job a teacher started (an upload, a
 * pasted link, a key save, a cut), still waiting or running, not yet filed.
 */
export const STOPPABLE_KINDS = ['separate', 'link', 'transpose', 'cut'];
export const canStopJob = (job) =>
	Boolean(job) &&
	(job.status === 'queued' || job.status === 'claimed') &&
	!job.pointerId &&
	STOPPABLE_KINDS.includes(job.kind || 'separate');

/** What Stop throws away, in the confirmation's words. A key save or a cut
 *  is made FROM a track that stays; an add throws away its own recording. */
export const stopConfirmText = (job) => {
	// Design pass 4 S5.2: name what goes AND what stays, so nobody wonders
	// whether Stop takes the song, the video or the notes with it.
	const fromTrack = job && (job.kind === 'transpose' || job.kind === 'cut');
	const fromLink = job && job.kind === 'link';
	return {
		head: 'Stop making this track?',
		thrown: fromTrack ? 'the half-finished new file' : fromLink ? 'the downloaded audio and the half-finished track' : 'the uploaded recording and the half-finished track',
		kept: fromTrack ? 'the original track, the words, your notes' : fromLink ? 'the saved video, the words, your notes' : 'the words, the videos, your notes',
	};
};

/** A stopped job's line stays this long ("Stopped. Nothing was saved."). */
export const STOPPED_VISIBLE_MS = 90 * 1000;

/** working | done | failed | stopped — drives the row's icon and colour. */
export const jobTone = (job) => {
	if (!job) return 'working';
	if (job.status === 'failed') return 'failed';
	if (job.status === 'cancelled') return 'stopped';
	if (job.status === 'done' && job.pointerId) return 'done';
	return 'working';
};

/* ---- Save as the student's key (build step 4) ---------------------------- */

// Mirrors functions/trackJobs.js keyLabel/MAX_SEMITONES (pinned by the test).
export const MAX_KEY_SEMITONES = 6;
export const keyLabel = (semitones) => {
	const n = Number(semitones);
	if (!Number.isInteger(n) || n === 0) return null;
	return n > 0 ? `Up ${n}` : `Down ${-n}`;
};

/**
 * What "Save as the student's key" would render, or null when there is
 * nothing to save. ALWAYS from the original: a teacher playing the saved
 * "Up 2" file who nudges it up one more saves "Up 3" rendered from the
 * original recording, never a copy of a copy. Only a file with a Storage copy
 * can be rendered (the same condition as the key control playing it).
 */
export const keySaveFor = (files = [], current = null, delta = 0) => {
	if (!current || !Number.isInteger(delta)) return null;
	const list = Array.isArray(files) ? files : [];
	const original = current.keyOf ? list.find((f) => f && f.storagePath === current.keyOf) || null : current;
	if (!original || !original.storagePath || typeof original.storageUrl !== 'string') return null;
	const semitones = (Number.isInteger(current.semitones) ? current.semitones : 0) + delta;
	if (semitones === 0 || Math.abs(semitones) > MAX_KEY_SEMITONES) return null;
	if (delta === 0) return null; // already this key: nothing to save
	return { source: original, semitones, label: keyLabel(semitones) };
};

/** The one line a teacher reads. Says WHERE the work is happening, so a
 *  fallback (or a Mac that is off) is visible rather than a silent wait. */
export const jobStatusText = (job, { anyMacOn = true } = {}) => {
	if (!job) return '';
	if (job.status === 'cancelled') return 'Stopped. Nothing was saved.';
	if (job.kind === 'songLyrics') {
		switch (job.status) {
			case 'queued':
				return anyMacOn ? 'Listening to the lyric video to check the words…' : 'The words will be checked against the lyric video when a studio Mac is on.';
			case 'claimed':
				return `Listening to the lyric video${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done':
				return job.pointerId ? 'Words checked against the lyric video.' : 'Checking the words: filing…';
			case 'failed':
				return `The lyric video could not be checked. ${TELL_ANTHONY}`;
			default:
				return '';
		}
	}
	if (job.kind === 'lyrics') {
		switch (job.status) {
			case 'queued':
				return anyMacOn ? 'Timing the words for the second screen…' : 'The words will be timed for the second screen when a studio Mac is on.';
			case 'claimed':
				return `Timing the words for the second screen${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done': {
				if (!job.pointerId) return 'Timing the words: filing…';
				const found = job.result && job.result.lyrics;
				if (!found) return 'No published words found for this song, so the second screen will not scroll it.';
				return found.unverified
					? 'Words timed for the second screen (heard by ear: check them).'
					: 'Words timed for the second screen.';
			}
			case 'failed':
				return `The words could not be timed. ${TELL_ANTHONY}`;
			default:
				return '';
		}
	}
	if (job.kind === 'transpose') {
		const what = `${job.source?.variant || 'Track'} ${keyLabel(job.semitones) || ''}`.trim();
		switch (job.status) {
			case 'queued':
				return anyMacOn ? `Saving ${what}: waiting for a studio Mac…` : `Saving ${what}: no studio Mac is on, sending it to the cloud…`;
			case 'claimed':
				return job.claim?.kind === 'cloud'
					? `Saving ${what} in the cloud…`
					: `Saving ${what}${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done':
				return job.pointerId ? `Saved ${what}. The family can play and download it from their Songs page.` : `Saving ${what}: filing…`;
			case 'failed':
				return `${what} could not be saved. ${TELL_ANTHONY}`;
			default:
				return what;
		}
	}
	if (job.kind === 'key') {
		switch (job.status) {
			case 'queued':
				return anyMacOn ? 'Finding the key…' : 'The key will be found when a studio Mac is on.';
			case 'claimed':
				return `Finding the key${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done': {
				const k = job.result && job.result.musicalKey;
				return job.pointerId && k ? `Key found: ${k.label || `${k.tonic} ${k.mode}`} (detected).` : 'Finding the key: filing…';
			}
			case 'failed':
				return `The key could not be found. ${TELL_ANTHONY}`;
			default:
				return '';
		}
	}
	if (job.kind === 'cut') {
		const secs = Math.round(Number(job.end) - Number(job.start));
		const what = `${job.source?.variant || 'Track'}, a ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')} cut`;
		switch (job.status) {
			case 'queued':
				return anyMacOn ? `Saving the ${what}: waiting for a studio Mac…` : `Saving the ${what}: a studio Mac has to be on. It starts as soon as one is.`;
			case 'claimed':
				return `Saving the ${what}${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done':
				return job.pointerId ? `Saved the ${what}. The family can play and download it from their Songs page.` : `Saving the ${what}: filing…`;
			case 'failed':
				return `The ${what} could not be saved. ${TELL_ANTHONY}`;
			default:
				return what;
		}
	}
	if (job.kind === 'link') {
		const name = job.songName || 'Track';
		const doing = job.removeVocals ? 'downloading and removing the vocals' : 'downloading';
		switch (job.status) {
			case 'queued':
				return anyMacOn ? `${name}: waiting for a studio Mac to fetch the link…` : `${name}: a studio Mac has to be on to fetch a link. It starts as soon as one is.`;
			case 'claimed':
				return `${name}: ${doing}${job.claim?.workerName ? ` on ${job.claim.workerName}` : ''}…`;
			case 'done':
				if (!job.pointerId) return `${name}: filing…`;
				return job.removeVocals ? `${name}: added from the link, with a no-vocals Track.` : `${name}: added from the link.`;
			case 'failed':
				return `${name}: the link could not be fetched. ${TELL_ANTHONY}`;
			default:
				return name;
		}
	}
	const name = job.source?.fileName || job.songName || 'Track';
	switch (job.status) {
		case 'queued':
			if (!job.removeVocals) return `${name}: adding…`;
			return anyMacOn ? `${name}: waiting for a studio Mac to remove the vocals…` : `${name}: no studio Mac is on, sending it to the cloud…`;
		case 'claimed': {
			if (job.claim?.kind === 'cloud') return `${name}: removing the vocals in the cloud (about as long as the song)…`;
			const where = job.claim?.workerName ? ` on ${job.claim.workerName}` : '';
			return `${name}: removing the vocals${where}…`;
		}
		case 'done':
			if (!job.pointerId) return `${name}: filing…`;
			return job.removeVocals ? `${name}: added, with a no-vocals Track.` : `${name}: added.`;
		case 'failed':
			return `${name}: the vocals could not be removed. ${TELL_ANTHONY}`;
		default:
			return name;
	}
};

/** A job just landed (filed) that was not filed at the last snapshot — the
 *  signal to re-read the student's tracks. */
export const newlyFiledIds = (prev = [], next = []) => {
	const was = new Set((prev || []).filter((j) => j && j.pointerId).map((j) => j.id));
	return (next || []).filter((j) => j && j.pointerId && !was.has(j.id)).map((j) => j.id);
};

/**
 * "Save as a cut" (2026-09-28): which files one save renders. A lesson
 * track's Reference, Track and Vocals share one timeline, so they are cut
 * together (Rehearsal mode keeps working on the cut); anything else is cut
 * alone. Never a superseded file.
 */
const CUT_SET = new Set(['reference', 'track', 'vocals']);
export const cutSetFor = (file, files = []) => {
	if (!file || !file.storagePath) return [];
	const v = String(file.variant || '').toLowerCase();
	if (!file.jobId || file.key || !CUT_SET.has(v)) return [file];
	const set = (files || []).filter(
		(f) => f && !f.superseded && !f.key && f.storagePath && f.jobId === file.jobId && CUT_SET.has(String(f.variant || '').toLowerCase())
	);
	return set.length ? set : [file];
};
